import React, { useEffect, useState } from "react";
import api, { formatRupiah, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Handshake, Plus, Users } from "lucide-react";
import { toast } from "sonner";

export default function CoBroke() {
  const [deals, setDeals] = useState([]);
  const [listings, setListings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    listing_id: "", partner_agent_id: "", buyer_lead_id: "",
    listing_share: 50, notes: "",
  });

  const load = async () => {
    const [d, l, a, le] = await Promise.all([
      api.get("/cobroke"), api.get("/listings?mine=true"),
      api.get("/agents"), api.get("/leads"),
    ]);
    setDeals(d.data); setListings(l.data); setAgents(a.data); setLeads(le.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cobroke", {
        listing_id: form.listing_id,
        partner_agent_id: form.partner_agent_id,
        buyer_lead_id: form.buyer_lead_id || null,
        listing_share: Number(form.listing_share),
        buyer_share: 100 - Number(form.listing_share),
        notes: form.notes,
      });
      toast.success("Co-broke dibuat");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/cobroke/${id}/status?status=${status}`);
      toast.success("Status diperbarui");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const partner = agents.find((a) => a.id === form.partner_agent_id);
  const listing = listings.find((l) => l.id === form.listing_id);

  return (
    <div className="space-y-6" data-testid="cobroke-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Kolaborasi</span>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1">Co-Broke Deals</h1>
          <p className="text-slate-400 text-sm mt-1">Bagi komisi antar agent dengan rasio transparan.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-cobroke-btn" className="bg-gradient-to-r from-[#FF5722] to-[#F97316] hover:from-[#F97316] hover:to-[#EA580C] font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Buat Co-Broke
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141C2E] border-white/10 text-slate-100 max-w-xl">
            <DialogHeader><DialogTitle>Co-Broke Baru</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Listing (milik Anda)</Label>
                <Select value={form.listing_id} onValueChange={(v) => setForm({ ...form, listing_id: v })}>
                  <SelectTrigger data-testid="cobroke-listing-select" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue placeholder="Pilih listing" /></SelectTrigger>
                  <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100 max-h-72">
                    {listings.map((l) => <SelectItem key={l.id} value={l.id}>{l.title} — {formatRupiah(l.price)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Partner Agent (buyer-side)</Label>
                <Select value={form.partner_agent_id} onValueChange={(v) => setForm({ ...form, partner_agent_id: v })}>
                  <SelectTrigger data-testid="cobroke-agent-select" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue placeholder="Pilih agent" /></SelectTrigger>
                  <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100 max-h-72">
                    {agents.length === 0 && <SelectItem value="none" disabled>Belum ada agent lain terdaftar</SelectItem>}
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} {a.agency_name ? `· ${a.agency_name}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lead Pembeli (opsional)</Label>
                <Select value={form.buyer_lead_id} onValueChange={(v) => setForm({ ...form, buyer_lead_id: v })}>
                  <SelectTrigger data-testid="cobroke-lead-select" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue placeholder="— tidak ada —" /></SelectTrigger>
                  <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100 max-h-72">
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Pembagian Komisi</Label>
                  <div className="text-xs text-slate-400">Listing <b className="text-orange-400">{form.listing_share}%</b> · Buyer <b className="text-blue-400">{100 - form.listing_share}%</b></div>
                </div>
                <Slider
                  min={10} max={90} step={5} value={[form.listing_share]}
                  onValueChange={(v) => setForm({ ...form, listing_share: v[0] })}
                  className="mt-3" data-testid="cobroke-slider"
                />
              </div>
              <div><Label>Catatan</Label><Textarea rows={3} value={form.notes} data-testid="cobroke-notes" onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0D1322] border-white/10 resize-none" placeholder="Komisi 2% dari harga jual, dll" /></div>

              {listing && partner && (
                <div className="rounded-lg bg-[#0D1322] border border-orange-500/20 p-3 text-xs text-slate-300">
                  <div className="font-semibold text-slate-100">Preview Split</div>
                  <div>Listing — <b>{listing.agent_name}</b>: {form.listing_share}%</div>
                  <div>Buyer — <b>{partner.name}</b>: {100 - form.listing_share}%</div>
                </div>
              )}
              <Button type="submit" data-testid="cobroke-submit" className="w-full bg-orange-500 hover:bg-orange-600 font-semibold">Simpan Co-Broke</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {deals.length === 0 ? (
        <div className="rounded-xl bg-[#141C2E] border border-white/5 p-14 text-center">
          <Handshake className="w-10 h-10 mx-auto text-slate-500 mb-3" />
          <h3 className="font-semibold">Belum ada deal co-broke</h3>
          <p className="text-sm text-slate-400 mt-1">Mulai kolaborasi dengan agent lain di platform.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {deals.map((d) => (
            <div key={d.id} data-testid={`cobroke-card-${d.id}`} className="rounded-xl bg-[#141C2E] border border-white/5 p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-100">{d.listing_title}</div>
                  <div className="text-xs text-slate-400 mt-1">{formatRupiah(d.listing_price)}</div>
                </div>
                <Select value={d.status} onValueChange={(v) => changeStatus(d.id, v)}>
                  <SelectTrigger data-testid={`cobroke-status-${d.id}`} className="h-7 w-[110px] bg-[#0D1322] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-lg bg-[#0D1322] border border-orange-500/20">
                  <div className="text-slate-500 uppercase text-[10px] tracking-widest">Listing Agent</div>
                  <div className="font-semibold text-slate-100 mt-0.5">{d.listing_agent_name}</div>
                  <div className="text-orange-400 font-bold mt-1">{d.listing_share}%</div>
                </div>
                <div className="p-3 rounded-lg bg-[#0D1322] border border-blue-500/20">
                  <div className="text-slate-500 uppercase text-[10px] tracking-widest">Buyer Agent</div>
                  <div className="font-semibold text-slate-100 mt-0.5">{d.buyer_agent_name}</div>
                  <div className="text-blue-400 font-bold mt-1">{d.buyer_share}%</div>
                </div>
              </div>
              {d.notes && <div className="mt-3 text-xs text-slate-400 italic">"{d.notes}"</div>}
            </div>
          ))}
        </div>
      )}

      {agents.length === 0 && (
        <div className="rounded-xl bg-blue-500/5 border border-blue-500/20 p-4 text-sm text-blue-200 flex items-center gap-2">
          <Users className="w-4 h-4" /> Belum ada agent lain terdaftar. Undang teman Anda daftar untuk mulai co-broke.
        </div>
      )}
    </div>
  );
}
